import React, { useEffect, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "usehooks-ts";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";

import useAuthContext from "../../providers/authProvider";
import Table from "../../components/Table";

import { calculateRowPosition, calculateColumnPosition } from "../../utils/positionCalculations";
import { organizationsClubs, createClubGroup, getClubGroups, assignOrganizationClubGroup, updateClubGroup, deleteClubGroup } from '../../services/organizations';

import GroupDialog from "../../components/GroupDialog";

const OrganizationClubsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setClub } = useAuthContext();

  const [groupData, setGroupData] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [groupBy, setGroupBy] = useLocalStorage("clubsGroupBy", "all");

  const fetchClubs = async () => {
    const { clubs } = await organizationsClubs();
    setClubs(clubs);
  };

  useEffect(() => {
    fetchClubs();
  }, []);

  const selectClub = async (clubId) => {
    Cookies.set("club_id", clubId);
    await setClub();
    navigate(`/club_admin/club/${clubId}`);
  };

  return (
    <>
      <div className="teams">
        <div className="teams-header">
          <div className="teams-header-text">{t("clubs.index.title")}</div>
          {groupBy === "category" && (
            <div className="teams-btn">
              <button className="button button-add" onClick={() => setGroupData({})}>
                {t("teams.add_group")}
              </button>
            </div>
          )}
        </div>

        <div className="teams-groups">
          <div
            className={`teams-groups-option pointer ${groupBy === "all" ? "active" : ""}`}
            onClick={() => setGroupBy("all")}
          >
            {t("clubs.index.all")}
          </div>
          <div
            className={`teams-groups-option pointer ${groupBy === "category" ? "active" : ""}`}
            onClick={() => setGroupBy("category")}
          >
            {t("teams.group")}
          </div>
        </div>

        <div className="teams-list">
          {groupBy === "all" ? (
            <ClubsAll clubs={clubs} selectClub={selectClub} />
          ) : (
            <ClubsByCategory
              {...{
                clubs,
                groupData,
                setGroupData,
                selectClub,
                fetchRows: fetchClubs
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default OrganizationClubsList;

const ClubsAll = ({ clubs, selectClub }) => {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      {
        header: "",
        accessorKey: "logo",
        cell: (info) => (
          <img
            src={info.getValue()}
            alt="logo"
            style={{ width: 32, height: 32 }}
          />
        ),
        meta: { style: { width: 40 } },
      },
      {
        header: t("common.table_name"),
        accessorKey: "name",
        cell: (info) => (
          <div
            onClick={() => selectClub(info.row.original.id)}
          >
            {info.getValue()}
          </div>
        ),
      },
      { header: t("common.teams"), accessorKey: "teams_count" },
      { header: t("common.members"), accessorKey: "members_count" },
      {
        header: t("common.premiums"),
        accessorKey: "premiums",
        cell: (info) => (
          <div className="flex">
            {info.row.original.premiums.map((premium) => (
              <span
                key={premium?.title}
                className="premiums-circle"
                style={{ backgroundColor: "#" + premium?.color }}
              ></span>
            ))}
          </div>
        ),
      },
      {
        id: "actions",
        header: " ",
        cell: (info) => (
          <div className="flex-end ai-center">
            <i
              className="icon icon-login pointer"
              onClick={() => selectClub(info.row.original.id)}
            ></i>
          </div>
        ),
      },
    ],
    []
  );

  return <Table {...{ data: clubs, columns }} />;
};

const ClubsByCategory = ({ clubs, groupData, setGroupData, selectClub, fetchRows }) => {
  const { t } = useTranslation();
  const [result, setResult] = useState({});
  const [groupClubs, setGroupClubs] = useState([]);

  useEffect(() => {
      const result = clubs.reduce((acc, club) => {
        const key = club.club_group_id ?? "null";
        (acc[key] = acc[key] || []).push(club);
        return acc;
      }, {});
  
      for (const key in result) {
        result[key].sort((a, b) => parseFloat(a.position) - parseFloat(b.position));
      }
  
      setResult(result);
    }, [clubs]);
  
    const fetchGroups = async () => {
      try {
        const { groups = [] } = await getClubGroups();
        groups.push({ id: "null", name: t("teams.group_title"), position: "0" });
        groups.sort((a, b) => a.position - b.position);
  
        setGroupClubs(groups);
      } catch (error) {
        console.error("Error fetching clubs:", error);
      }
    };
  
    useEffect(() => {
      fetchGroups();
    }, []);

 const onDragEnd = async ({ source, destination, type }) => {
    if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) return;

     if (type === "column") {
      const filteredGroups = groupClubs.filter(group => group.id !== "null");
      const position = calculateColumnPosition(destination.index, filteredGroups).toFixed(3);

      const [movedItem] = filteredGroups.splice(source.index, 1);
      filteredGroups.splice(destination.index, 0, { ...movedItem, position });
      filteredGroups.push(groupClubs.find(group => group.id === "null"));

      setGroupClubs(filteredGroups);
      try {
        await updateClubGroup(movedItem.id, { name: movedItem.name, position });
      } catch (error) {
        console.error("Error updating group positions:", error);
      }
    } else {
      const startColumn = result[source.droppableId] || [];
      const endColumn = result[destination.droppableId] || [];
      const [movedItem] = startColumn.splice(source.index, 1);

      const isSameColumn = startColumn === endColumn;
      const columnToUpdate = isSameColumn ? startColumn : endColumn;
      const position = calculateRowPosition(destination.index, columnToUpdate).toFixed(3);
      columnToUpdate.splice(destination.index, 0, { ...movedItem, position });

      setResult(prevResult => ({
        ...prevResult,
        [source.droppableId]: startColumn.map((item, index) => ({ ...item, displayPosition: index + 1 })),
        [destination.droppableId]: endColumn.map((item, index) => ({ ...item, displayPosition: index + 1 })),
      }));

      const normalizedId = destination.droppableId === "null" ? null : destination.droppableId;

      try {
        await assignOrganizationClubGroup(movedItem.clubs_organization_id, normalizedId, position);
      } catch (error) {
        console.error("Error updating club group:", error);
      }
    }
  };

  const renderHeader = useMemo(
    () => (
      <div className="group-clubs-item header">
        <div className="group-icon" />
        <div className="group-name">{t("common.table_name")}</div>
        <div className="group-teams">{t("common.teams")}</div>
        <div className="group-members">{t("common.members")}</div>
        <div className="group-premiums">{t("common.premiums")}</div>
        <div className="group-actions" />
      </div>
    ),
    [t]
  );

  const renderClubRow = (club, index) => (
    <Draggable key={club.id} draggableId={'group-' + club.id.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`group-clubs-item ${snapshot.isDragging ? "dragging" : ""}`}
        >
          <div className="group-icon" {...provided.dragHandleProps}>
            <i className="icon icon-options-vertical"></i>
          </div>
          <div className="group-name ellipsis" onClick={() => editTeam(club)}>
            {club.name}
          </div>
          <div className="group-teams">{club.teams_count}</div>
          <div className="group-members">{club.members_count}</div>
          <div className="group-premiums">
            {club.premiums.map((premium) => (
              <span
                key={premium?.title}
                className="premiums-circle"
                style={{ backgroundColor: "#" + premium?.color }}
              ></span>
            ))}
          </div>
          <div className="group-actions">
            <i
              className="icon icon-login pointer"
              onClick={() => selectClub(club.id)}
            ></i>
          </div>
        </div>
      )}
    </Draggable>
  );

  const ClubGroup = ({ group }) => (
    <Droppable droppableId={group.id.toString()} type="task">
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={`group-teams ${snapshot.isDraggingOver ? "droppable-highlight" : ""}`}
        >
          {renderHeader}
          {(result[group.id] || []).map((club, index) =>
            renderClubRow(club, index)
          )}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="teams-list-category">
          <div className="teams-left-column">
            <Droppable droppableId="left-column" direction="vertical" type="column">
              {provided => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="teams-list-category-left">
                  {groupClubs.filter(group => group.id !== "null").map((group, index) => (
                      <Draggable key={group.id} draggableId={group.id.toString()} index={index}>
                        {provided => (
                          <div ref={provided.innerRef} {...provided.draggableProps} className="teams-list-category-item">
                            <div {...provided.dragHandleProps} className="teams-list-category-name">
                              {group.name}
                              <i className="icon icon-pencil edit-icon pointer" onClick={() => setGroupData(group)}></i>
                            </div>
                            <ClubGroup group={group} />
                          </div>
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>

          <div className="teams-right-column">
            <div className="teams-list-category-right">
              {groupClubs.filter((group) => group.id === "null").map((group) => (
                  <div key={group.id} className="teams-list-category-item">
                    <div className="teams-list-category-name">{group.name}</div>
                    <ClubGroup group={group} />
                  </div>
              ))}
            </div>
          </div>
        </div>
      </DragDropContext>

      {groupData && 
        <GroupDialog 
          groupData={groupData} 
          close={() => setGroupData(null)} 
          fetchGroups={fetchGroups} 
          createGroup={createClubGroup} 
          updateGroup={updateClubGroup} 
          deleteGroup={deleteClubGroup}
          fetchRows={fetchRows}
        />
      }
    </>
  );
};
